import {inject, Injectable} from '@angular/core';
import recommend from '@algolia/recommend';
import {TrendingQuery} from '@algolia/recommend/dist/recommend';
import algoliasearch from 'algoliasearch/lite';
import {captureException} from '@sentry/angular';
import {AlgoliaService, AlgoliaSettings} from './algolia.service';
import {AlgoliaWineProduct} from '@core/types/algolia-wine-product';

const FACET_NAME_PATTERN = /^[A-Za-z0-9_.:-]+$/;

@Injectable({
  providedIn: 'root'
})
export class AlgoliaRecommendService {
  private algoliaService = inject(AlgoliaService);

  async getTrendingProducts(algoliaSettings: AlgoliaSettings, maxRecommendations: number, filters: string = '', facetName: string = '', facetValue: string = ''): Promise<AlgoliaWineProduct[]> {
    const trendingQuery: TrendingQuery = {
      indexName: algoliaSettings.indexName,
      model: 'trending-items',
      maxRecommendations: +maxRecommendations,
      queryParameters: {
        filters: this.buildFiltersString(filters, facetName, facetValue)
      }
    };

    if (facetName && facetValue && FACET_NAME_PATTERN.test(facetName)) {
      (trendingQuery as any).facetName = facetName;
      (trendingQuery as any).facetValue = facetValue;
    } else if (facetName && !FACET_NAME_PATTERN.test(facetName)) {
      console.warn('AlgoliaRecommendService.getTrendingProducts: invalid facetName ignored');
    }

    const hits = await this.fetchFromRecommendApi(algoliaSettings, trendingQuery);
    if (hits.length > 0) {
      return hits;
    }

    return this.getFallbackProducts(algoliaSettings, maxRecommendations, filters, facetName, facetValue);
  }

  private async fetchFromRecommendApi(algoliaSettings: AlgoliaSettings, trendingQuery: TrendingQuery): Promise<AlgoliaWineProduct[]> {
    const recommendClient = recommend(algoliaSettings.appId, algoliaSettings.apiKey);
    try {
      const trendingItems = await recommendClient.getTrendingItems([trendingQuery]);
      const hits = (trendingItems.results?.[0] as any)?.hits;

      if (!Array.isArray(hits)) {
        return [];
      }

      return hits.map((hit: any) => this.algoliaService.hitToWineProduct(hit));
    } catch (error) {
      console.error('AlgoliaRecommendService.getTrendingProducts: recommend client failed, falling back to index search', error);
      captureException(error);
    }
    return [];
  }

  private async getFallbackProducts(
    algoliaSettings: AlgoliaSettings,
    maxRecommendations: number,
    filters: string = '',
    facetName: string = '',
    facetValue: string = ''
  ): Promise<AlgoliaWineProduct[]> {
    const filtersString = this.buildFiltersString(filters, facetName, facetValue);

    try {
      const client = algoliasearch(algoliaSettings.appId, algoliaSettings.apiKey);
      const index = client.initIndex(algoliaSettings.indexName);
      const result = await index.search('', {filters: filtersString, hitsPerPage: maxRecommendations});

      return result.hits.map((hit: any) => {
        return this.algoliaService.hitToWineProduct(hit);
      });
    } catch (error) {
      console.error('AlgoliaRecommendService.getFallbackProducts: index search failed, returning empty recommendations', error);
      captureException(error);
      return [];
    }
  }

  private buildFiltersString(filters: string = '', facetName: string = '', facetValue: string = ''): string {
    let filtersString = 'commerce_stock_bool:1 AND on_discount:1 AND is_salable_qty_more_than_default_qty:true AND attribute_set:Vin';

    if (filters) {
      filtersString += ` AND ${filters}`;
    }

    const safeFacetName = FACET_NAME_PATTERN.test(facetName) ? facetName : '';
    if (facetName && !safeFacetName) {
      console.warn('AlgoliaRecommendService.buildFiltersString: invalid facetName ignored');
    }
    if (safeFacetName && facetValue) {
      const escapedFacetValue = facetValue.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      filtersString += ` AND ${safeFacetName}:"${escapedFacetValue}"`;
    }

    return filtersString;
  }

}
